import React, { FC, useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import {
	ANIMATION_TIME_MS,
	MAXIMUM_PROGRESS_VALUE,
	MOVE_INTERVAL_MS,
	PROGRESS_SPEED,
	ProgressActionType,
	PROGRESSES_INITIAL_STATE,
	WRONG_ID_PARAM_IN_FINISH_ALERT_MESSAGE,
	WRONG_ID_PARAM_IN_RESET_ALERT_MESSAGE,
} from './const';
import { TopProgressBar } from './TopProgressBar';
import { progressesReducer } from './utils';
import { IntervalIdRefInstance, TopProgressProviderValue } from './types';
import { useMounted } from '../../hooks/useMounted';
import { isUndefined } from '../../predicates/type';
import { IS_LOCAL_DEVELOPMENT } from '../../const/env';
import { useCallbackRef } from '../../hooks/useCallbackRef';
import { createProvider } from '../../utils/createProvider';

const [Provider, useTopProgressBarProvider] = createProvider<TopProgressProviderValue>({
	contextName: 'TopProgressBarContext',
});

let progressId = 0;

const TopProgressBarProvider: FC<React.PropsWithChildren> = ({ children }) => {
	const [progresses, dispatch] = useReducer(progressesReducer, PROGRESSES_INITIAL_STATE);
	const progress = progresses[0]?.value || 0;
	const intervalIdsRef = useRef<IntervalIdRefInstance[]>([]);
	const { mountedRef } = useMounted();

	const clearProgressInterval = (id: number) => {
		const interval = intervalIdsRef.current.find((i) => i.progressId === id)?.intervalId;

		if (!isUndefined(interval)) {
			clearInterval(interval);
			intervalIdsRef.current = intervalIdsRef.current.filter((i) => i.intervalId !== interval);
		}
	};

	const clearAllProgressIntervals = () => {
		intervalIdsRef.current.forEach((i) => {
			clearInterval(i.intervalId);
			intervalIdsRef.current = [];
		});
	};

	const completeAllProgresses = () => {
		dispatch({
			type: ProgressActionType.SetAllProgressValues,
			payload: {
				value: MAXIMUM_PROGRESS_VALUE,
			},
		});
	};

	const completeProgress = (id: number) => {
		dispatch({
			type: ProgressActionType.SetProgressValue,
			payload: {
				id,
				value: MAXIMUM_PROGRESS_VALUE,
			},
		});
	};

	const removeAllProgresses = () => {
		dispatch({ type: ProgressActionType.RemoveAllProgresses });
	};

	const removeProgress = (id: number) => {
		dispatch({
			type: ProgressActionType.RemoveProgress,
			payload: { id },
		});
	};

	const makeProgressActive = useCallbackRef((id: number) => {
		let width = 0;

		const handleMove = () => {
			if (!mountedRef.current) {
				clearAllProgressIntervals();

				return;
			}

			width += PROGRESS_SPEED;

			dispatch({
				type: ProgressActionType.SetProgressValue,
				payload: {
					id,
					value: Math.round((Math.atan(width) / (Math.PI / 2)) * 100 * 1000) / 1000,
				},
			});
		};

		dispatch({
			type: ProgressActionType.MakeProgressActive,
			payload: { id },
		});

		intervalIdsRef.current = [
			...intervalIdsRef.current,
			{
				progressId: id,
				intervalId: setInterval(handleMove, MOVE_INTERVAL_MS),
			},
		];
	});

	useEffect(() => {
		if (isUndefined(progresses[0]) || progresses[0].isActive) {
			return;
		}

		makeProgressActive(progresses[0].id);
	}, [progresses, makeProgressActive]);

	const start = useCallback(() => {
		const id = ++progressId;

		dispatch({
			type: ProgressActionType.AddProgress,
			payload: { id },
		});

		return id;
	}, []);

	const finishAll = useCallbackRef(() => {
		clearAllProgressIntervals();
		completeAllProgresses();
		setTimeout(removeAllProgresses, ANIMATION_TIME_MS);
	});

	const finish = useCallbackRef((id: number) => {
		if (isUndefined(id)) {
			if (IS_LOCAL_DEVELOPMENT) {
				alert(WRONG_ID_PARAM_IN_FINISH_ALERT_MESSAGE);
			}

			return;
		}

		clearProgressInterval(id);
		completeProgress(id);
		setTimeout(() => removeProgress(id), ANIMATION_TIME_MS);
	});

	const resetAll = useCallbackRef(() => {
		clearAllProgressIntervals();
		dispatch({ type: ProgressActionType.RemoveAllProgresses });
	});

	const reset = useCallbackRef((id: number) => {
		if (isUndefined(id)) {
			if (IS_LOCAL_DEVELOPMENT) {
				alert(WRONG_ID_PARAM_IN_RESET_ALERT_MESSAGE);
			}

			return;
		}

		clearProgressInterval(id);
		removeProgress(id);
	});

	const value = useMemo<TopProgressProviderValue>(
		() => ({
			start,
			finishAll,
			finish,
			resetAll,
			reset,
		}),
		[start, finishAll, finish, resetAll, reset],
	);

	return (
		<Provider value={value}>
			{children}
			{progress !== 0 && <TopProgressBar progress={progress} />}
		</Provider>
	);
};

export { TopProgressBarProvider, useTopProgressBarProvider };
